<template>
  <div class="cc-checkout">
    <h1 class="text-h5 mb-4">
      {{ t('checkout.headline') }}
    </h1>

    <div
      v-if="loading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        :aria-label="t('checkout.headline')"
      />
    </div>

    <p
      v-else-if="notMainUser"
      data-test="checkout-not-main-user"
    >
      {{ t('checkout.notMainUser') }}
    </p>

    <p
      v-else-if="checkout?.state === 'unavailable'"
      data-test="checkout-unavailable"
    >
      {{ t('checkout.unavailable') }}
    </p>

    <!-- Contract active: the tenant moves to its chosen address. -->
    <div
      v-else-if="step === 'done'"
      data-test="checkout-done"
    >
      <h2 class="text-h6 mb-2">
        {{ t('checkout.done.headline') }}
      </h2>
      <p class="mb-4">
        {{ t('checkout.done.info', {url: tenantUrl}) }}
      </p>
      <v-btn
        color="primary"
        :href="tenantUrl"
      >
        {{ t('checkout.done.open') }}
      </v-btn>
    </div>

    <!-- Ordered: the direct debit is missing. -->
    <div
      v-else-if="step === 'mandate'"
      data-test="checkout-mandate"
    >
      <h2 class="text-h6 mb-2">
        {{ t('checkout.mandate.headline') }}
      </h2>
      <p class="mb-4">
        {{ t('checkout.mandate.info') }}
      </p>
      <cc-alert-error
        v-if="mandateUnavailable"
        :text="t('checkout.mandate.unavailable')"
      />
      <p
        v-if="waiting"
        class="mb-4"
      >
        {{ t('checkout.mandate.waiting') }}
      </p>
      <v-btn
        v-else
        color="primary"
        :loading="busy"
        data-test="checkout-mandate-start"
        @click="startMandate"
      >
        {{ t('checkout.mandate.start') }}
      </v-btn>
    </div>

    <!-- Review: agreement and data processing agreement exactly as concluded. -->
    <div
      v-else-if="step === 'review' && preview"
      data-test="checkout-review"
    >
      <h2 class="text-h6 mb-2">
        {{ t('checkout.review.headline') }}
      </h2>
      <h3 class="text-subtitle-1 mt-4">
        {{ t('checkout.review.agreement') }}
      </h3>
      <div class="cc-checkout-document mb-4">
        <section
          v-for="(paragraph, index) in preview.agreement"
          :key="`agreement-${index}`"
        >
          <h4>{{ paragraph.headline }}</h4>
          <div v-dompurify-html="paragraph.text" />
        </section>
      </div>
      <template v-if="preview.dataProcessingAgreement.length">
        <h3 class="text-subtitle-1">
          {{ t('checkout.review.dataProcessing') }}
        </h3>
        <div class="cc-checkout-document mb-4">
          <section
            v-for="(paragraph, index) in preview.dataProcessingAgreement"
            :key="`dpa-${index}`"
          >
            <h4>{{ paragraph.headline }}</h4>
            <div v-dompurify-html="paragraph.text" />
          </section>
        </div>
      </template>
      <p class="font-weight-bold mb-2">
        {{ t('checkout.modules.total') }}: {{ euro(preview.monthlyTotal) }}
      </p>
      <v-checkbox
        v-model="confirmations.agreementAccepted"
        :label="t('checkout.review.agreementAccepted')"
        hide-details
      />
      <v-checkbox
        v-if="preview.dataProcessingAgreement.length"
        v-model="confirmations.dataProcessingAccepted"
        :label="t('checkout.review.dataProcessingAccepted')"
        hide-details
      />
      <v-checkbox
        v-model="confirmations.businessCustomer"
        :label="t('checkout.review.businessCustomer')"
        hide-details
      />
      <v-text-field
        v-model="confirmations.confirmedByName"
        :label="t('checkout.review.confirmedByName')"
        class="mt-2"
      />
      <cc-alert-error
        v-if="textChanged"
        :text="t('checkout.review.textChanged')"
      />
      <div class="d-flex ga-3 mt-2">
        <v-btn
          variant="text"
          :disabled="busy"
          @click="step = 'form'"
        >
          {{ t('checkout.review.back') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!canOrder"
          :loading="busy"
          data-test="checkout-order"
          @click="order"
        >
          {{ t('checkout.review.order') }}
        </v-btn>
      </div>
    </div>

    <!-- Form: modules, address, invoice details. -->
    <v-form
      v-else-if="checkout"
      ref="formRef"
      data-test="checkout-form"
      @submit.prevent="review"
    >
      <h2 class="text-h6 mb-1">
        {{ t('checkout.modules.headline') }}
      </h2>
      <p class="text-body-2 mb-2">
        {{ t('checkout.modules.hint') }}
      </p>
      <v-checkbox
        v-for="module in checkout.modules"
        :key="module.key"
        v-model="draft.modules"
        :value="module.key"
        :label="`${module.label} – ${t('checkout.modules.perMonth', {price: euro(module.price)})}`"
        hide-details
      />
      <p
        v-if="checkout.baseFee"
        class="mt-2 mb-0"
      >
        {{ t('checkout.modules.baseFee') }}: {{ t('checkout.modules.perMonth', {price: euro(checkout.baseFee)}) }}
      </p>
      <p
        v-if="checkout.storageBaseGb"
        class="mb-0"
      >
        {{ t('checkout.modules.storage', {gb: checkout.storageBaseGb}) }}
      </p>
      <p
        v-if="checkout.minimumTermMonths"
        class="mb-0"
      >
        {{ t('checkout.modules.minimumTerm', {months: checkout.minimumTermMonths}) }}
      </p>
      <p
        class="font-weight-bold mt-2 mb-6"
        data-test="checkout-total"
      >
        {{ t('checkout.modules.total') }}: {{ euro(monthlyTotal) }}
      </p>

      <h2 class="text-h6 mb-1">
        {{ t('checkout.subdomain.headline') }}
      </h2>
      <v-text-field
        v-model="draft.subdomain"
        :label="t('checkout.subdomain.label')"
        :suffix="t('checkout.subdomain.suffix')"
        :hint="subdomainMessage || t('checkout.subdomain.hint')"
        :error-messages="subdomainError"
        persistent-hint
        :rules="[required]"
        class="mb-6"
        @update:model-value="scheduleSubdomainCheck"
      />

      <h2 class="text-h6 mb-1">
        {{ t('checkout.company.headline') }}
      </h2>
      <v-text-field
        v-model="draft.companyName"
        :label="t('checkout.company.companyName')"
        :rules="[required]"
      />
      <v-row>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="draft.salutation"
            :label="t('checkout.company.salutation')"
            :rules="[required]"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="draft.firstname"
            :label="t('checkout.company.firstname')"
            :rules="[required]"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="draft.lastname"
            :label="t('checkout.company.lastname')"
            :rules="[required]"
          />
        </v-col>
      </v-row>
      <v-text-field
        v-model="draft.email"
        type="email"
        :label="t('checkout.company.email')"
        :rules="[required, validEmail]"
      />
      <v-text-field
        v-model="draft.mobile"
        :label="t('checkout.company.mobile')"
      />
      <v-row>
        <v-col
          cols="8"
          sm="9"
        >
          <v-text-field
            v-model="draft.street"
            :label="t('checkout.company.street')"
            :rules="[required]"
          />
        </v-col>
        <v-col
          cols="4"
          sm="3"
        >
          <v-text-field
            v-model="draft.streetNumber"
            :label="t('checkout.company.streetNumber')"
            :rules="[required]"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="4"
          sm="3"
        >
          <v-text-field
            v-model="draft.postalCode"
            :label="t('checkout.company.postalCode')"
            :rules="[required]"
          />
        </v-col>
        <v-col
          cols="8"
          sm="9"
        >
          <v-text-field
            v-model="draft.city"
            :label="t('checkout.company.city')"
            :rules="[required]"
          />
        </v-col>
      </v-row>
      <v-text-field
        :model-value="t('checkout.company.germany')"
        :label="t('checkout.company.country')"
        :hint="t('checkout.company.germanyOnly')"
        persistent-hint
        readonly
      />
      <v-btn
        type="submit"
        color="primary"
        class="mt-4"
        :loading="busy"
        data-test="checkout-review-button"
      >
        {{ t('checkout.review.button') }}
      </v-btn>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, reactive, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {ErrorDialog} from '@simpleforyou/client-core';
import {useApiClient} from '@/common/api/useApiClient';
import {CheckoutApi} from '@/common/api/CheckoutApi';
import {useStore} from '@/common/store/store';
import {useGoCardlessDropin} from '@/composables/checkout/useGoCardlessDropin';

// T00VOQ5DCZSJ — the coach concludes the contract inside the coaching. Prices,
// contract text and data processing agreement come live from SFY through the
// coaching-api; the IBAN is entered in GoCardless' own window only.

type CheckoutState = 'unavailable' | 'open' | 'pendingMandate' | 'active';
type Checkout = {
  state: CheckoutState;
  modules: {key: string; label: string; price: number}[];
  baseFee: number | null;
  storageBaseGb: number | null;
  minimumTermMonths: number | null;
  requestedSubdomain: string | null;
};
type Paragraph = {headline: string; text: string};
type Preview = {agreement: Paragraph[]; dataProcessingAgreement: Paragraph[]; monthlyTotal: number; textHash: string};
type Mandate = {state: 'ready' | 'completed' | 'unavailable'; flowId?: string; environment?: string; orderStatus: 'pendingMandate' | 'active'};
type ApiError = {response?: {status?: number; data?: {error?: string; reason?: string}}};

const STATUS_POLL_MS = 5000;
const SUBDOMAIN_CHECK_DELAY_MS = 400;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const {t} = useI18n();
const apiClient = useApiClient();
const store = useStore();
const dropin = useGoCardlessDropin();

const loading = ref(true);
const notMainUser = ref(false);
const busy = ref(false);
const checkout = ref<Checkout | null>(null);
const step = ref<'form' | 'review' | 'mandate' | 'done'>('form');
const preview = ref<Preview | null>(null);
const textChanged = ref(false);
const mandateUnavailable = ref(false);
const waiting = ref(false);
const subdomainError = ref<string>('');
const subdomainMessage = ref<string>('');
const formRef = ref<{validate: () => Promise<{valid: boolean}>} | null>(null);

const contact = store.contact as {salutation?: string; firstname?: string; lastname?: string} | undefined;
const draft = reactive({
  modules: [] as string[],
  subdomain: '',
  companyName: '',
  salutation: contact?.salutation ?? '',
  firstname: contact?.firstname ?? '',
  lastname: contact?.lastname ?? '',
  email: '',
  mobile: '',
  street: '',
  streetNumber: '',
  postalCode: '',
  city: '',
  country: 'de',
});
const confirmations = reactive({
  agreementAccepted: false,
  dataProcessingAccepted: false,
  businessCustomer: false,
  confirmedByName: `${draft.firstname} ${draft.lastname}`.trim(),
});

let subdomainTimer: ReturnType<typeof setTimeout> | undefined;
let pollTimer: ReturnType<typeof setTimeout> | undefined;

const euro = (value: number) => new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(value);
const required = (value: string) => !!value?.trim() || t('checkout.company.required');
const validEmail = (value: string) => EMAIL_PATTERN.test(value ?? '') || t('checkout.company.invalidEmail');

const monthlyTotal = computed(() => (checkout.value?.baseFee ?? 0) + (checkout.value?.modules ?? [])
  .filter((module) => draft.modules.includes(module.key))
  .reduce((sum, module) => sum + module.price, 0));

const tenantUrl = computed(() => `https://${checkout.value?.requestedSubdomain ?? draft.subdomain}.community-coaching.com`);

const canOrder = computed(() => confirmations.agreementAccepted
  && (confirmations.dataProcessingAccepted || !preview.value?.dataProcessingAgreement.length)
  && confirmations.businessCustomer
  && !!confirmations.confirmedByName.trim());

const errorCode = (error: unknown): ApiError['response'] => (error as ApiError)?.response;

const load = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get<Checkout>(CheckoutApi.api());
    checkout.value = response.data;
    if (response.data.state === 'pendingMandate') {
      step.value = 'mandate';
    } else if (response.data.state === 'active') {
      step.value = 'done';
    }
  } catch (error) {
    if (errorCode(error)?.data?.error === 'notMainUser') {
      notMainUser.value = true;
    } else {
      new ErrorDialog();
    }
  } finally {
    loading.value = false;
  }
};

const scheduleSubdomainCheck = () => {
  clearTimeout(subdomainTimer);
  subdomainError.value = '';
  subdomainMessage.value = '';
  subdomainTimer = setTimeout(checkSubdomain, SUBDOMAIN_CHECK_DELAY_MS);
};

const checkSubdomain = async () => {
  const subdomain = draft.subdomain.trim().toLowerCase();
  if (!subdomain) {
    return;
  }
  try {
    const response = await apiClient.post<{available: boolean; reason: 'invalid' | 'blocked' | 'taken' | null}>(CheckoutApi.subdomain(), {subdomain});
    subdomainError.value = response.data.available ? '' : t(`checkout.subdomain.reason.${response.data.reason}`);
    subdomainMessage.value = response.data.available ? t('checkout.subdomain.available') : '';
  } catch {
    // The live check is a help; the order validates again.
  }
};

const payload = () => ({...draft, subdomain: draft.subdomain.trim().toLowerCase(), mobile: draft.mobile.trim() || undefined});

const review = async () => {
  if (formRef.value && !(await formRef.value.validate()).valid) {
    return;
  }
  busy.value = true;
  try {
    const response = await apiClient.post<Preview>(CheckoutApi.preview(), payload());
    preview.value = response.data;
    textChanged.value = false;
    step.value = 'review';
  } catch (error) {
    const response = errorCode(error);
    if (response?.data?.error === 'subdomainUnavailable') {
      subdomainError.value = t(`checkout.subdomain.reason.${response.data.reason}`);
    } else {
      new ErrorDialog();
    }
  } finally {
    busy.value = false;
  }
};

const order = async () => {
  if (!preview.value) {
    return;
  }
  busy.value = true;
  try {
    await apiClient.post<Mandate>(CheckoutApi.order(), {
      ...payload(),
      ...confirmations,
      dataProcessingAccepted: confirmations.dataProcessingAccepted || !preview.value.dataProcessingAgreement.length,
      textHash: preview.value.textHash,
    });
    if (checkout.value) {
      checkout.value.requestedSubdomain = draft.subdomain.trim().toLowerCase();
    }
    step.value = 'mandate';
  } catch (error) {
    const response = errorCode(error);
    if (response?.data?.error === 'textChanged') {
      // Show the text as it is now; nothing was ordered.
      textChanged.value = true;
      await review();
      textChanged.value = true;
    } else {
      new ErrorDialog();
    }
  } finally {
    busy.value = false;
  }
};

const startMandate = async () => {
  busy.value = true;
  mandateUnavailable.value = false;
  try {
    const response = await apiClient.post<Mandate>(CheckoutApi.mandateStart(), {});
    if (response.data.orderStatus === 'active') {
      step.value = 'done';
      return;
    }
    if (response.data.state !== 'ready' || !response.data.flowId || !response.data.environment) {
      mandateUnavailable.value = true;
      return;
    }
    await dropin.open({
      billingRequestFlowID: response.data.flowId,
      environment: response.data.environment,
      onSuccess: () => {
        void completeMandate();
      },
    });
  } catch {
    mandateUnavailable.value = true;
  } finally {
    busy.value = false;
  }
};

const completeMandate = async () => {
  waiting.value = true;
  try {
    const response = await apiClient.post<Mandate>(CheckoutApi.mandateComplete(), {});
    if (response.data.orderStatus === 'active') {
      step.value = 'done';
      return;
    }
  } catch {
    // The status poll below picks it up; SFY also activates on its own.
  }
  pollStatus();
};

/** SFY activates once GoCardless confirmed the mandate; until then ask every few seconds. */
const pollStatus = () => {
  clearTimeout(pollTimer);
  pollTimer = setTimeout(async () => {
    try {
      const response = await apiClient.get<Mandate>(CheckoutApi.status());
      if (response.data.orderStatus === 'active') {
        waiting.value = false;
        step.value = 'done';
        return;
      }
    } catch {
      // Keep asking; a hiccup must not end the wait.
    }
    pollStatus();
  }, STATUS_POLL_MS);
};

onMounted(load);
onBeforeUnmount(() => {
  clearTimeout(subdomainTimer);
  clearTimeout(pollTimer);
});
</script>

<style lang="scss" scoped>
.cc-checkout {
  max-width: 760px;
}

.cc-checkout-document {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 12px 16px;
}
</style>
